import React, { useState } from 'react';
import { BrandPanel } from './BrandPanel';
import { PlusOutlined } from '@ant-design/icons';
import { ADMIN_ROUTES } from 'constants/routes';
import { RejectBrandModal } from './RejectBrandModal';
import { DeleteBrandModal } from './DeleteBrandModal';
import { BrandItemsTable } from './BrandItems';
import { useNavigate, useParams } from 'react-router-dom';
import { AddBrandModal } from 'pages/admin/brands/addBrand';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { Button, Col, Collapse, Form, HasAccess, Row, Typography } from 'components';
import { useLeadByIdQuery, useRejectLeadBrandMutation, useRemoveLeadBrandMutation, useUpdateLeadMutation } from 'api';
import styles from './styles.module.scss';
import { AddBrandToLead } from './AddBrandToLead';
import { PERMISSIONS } from 'constants/permissions';

const { Panel } = Collapse;
const { Text } = Typography;

// Example rejection reasons
const rejectionReasons = [
  { label: 'Duplicate', value: 'Duplicate' },
  { label: 'Price too high', value: 'Price too high' },
  { label: 'Delivery too long', value: 'Delivery too long' },
  { label: 'Other', value: 'Other' },
];

export const LeadBrands = () => {
  const { id } = useParams() as { id: string };
  const [brandRejectForm] = Form.useForm();
  const [brandAddForm] = Form.useForm();
  const { data: { brandIds: brandsMapping = {}, client, isFinalStatus } = { brandIds: {} }, isLoading } =
    useLeadByIdQuery({ id }, { skip: !id });
  const { handleSuccess, handleError } = useNotificationMessages();
  const [rejectBrand] = useRejectLeadBrandMutation();
  const [removeBrand, { isLoading: removeLoading }] = useRemoveLeadBrandMutation();
  const [rejectModal, setRejectModal] = useState<string | undefined>(undefined);
  const [deleteModal, setDeleteModal] = useState({ visible: false, brandId: '' });
  const [addBrandModalVisible, setAddBrandModalVisible] = useState(false);
  const [openAddBrandToLead, setOpenAddBrandToLead] = useState(false);
  const [updateLeadDetails] = useUpdateLeadMutation();
  const navigate = useNavigate();

  // Brand name click handler
  const handleBrandClick = (brandId: string) => {
    navigate(`${ADMIN_ROUTES.brand.view}/${brandId}/${ADMIN_ROUTES.brand.management}`);
  };

  // Reject brand
  const handleRejectBrand = async () => {
    const { reason, additionalInformation } = brandRejectForm.getFieldsValue();
    try {
      const { data, error } = await rejectBrand({
        id: id,
        reason,
        brandId: rejectModal,
        additionalInformation,
      });

      if (data?.success) {
        handleSuccess(`Brand was successfully rejected`);
      } else {
        handleError(error);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setRejectModal(undefined);
    }
  };

  // Delete brand
  const handleDeleteBrand = async () => {
    try {
      const { data, error } = await removeBrand({
        id: id,
        brandId: deleteModal.brandId,
      });

      if (data?.success) {
        handleSuccess(`Brand was successfully removed`);
      } else {
        handleError(error);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setDeleteModal({ visible: false, brandId: '' });
    }
  };

  const handleAddBrandToLead = async () => {
    try {
      const { brandId } = brandAddForm.getFieldsValue();
      const { data, error }: any = await updateLeadDetails({
        id,
        brandId,
        clientId: client?.id,
      });
      if (data?.success) {
        handleSuccess('The lead details were successfully updated.');
        setOpenAddBrandToLead(false);
      } else {
        handleError(error);
      }
    } catch (err) {
      handleError(err);
    }
  };

  return (
    <div className={styles.leadBrandsContainer}>
      <Row justify="space-between" align="middle" className={styles.headerRow}>
        <Col>
          <Text className={styles.brands_title}>Brands</Text>
        </Col>
        <Col>
          {isFinalStatus || isLoading ? (
            ''
          ) : (
            <HasAccess role={PERMISSIONS.leads.lead.write}>
              <HasAccess role={PERMISSIONS.brands.brand.read}>
                <Button
                  className={styles.statusBtn}
                  icon={<PlusOutlined />}
                  onClick={() => setOpenAddBrandToLead(true)}
                >
                  Add New
                </Button>
              </HasAccess>
            </HasAccess>
          )}
        </Col>
      </Row>
      <Collapse className={styles.collapse} accordion expandIconPosition="end">
        {Object.keys(brandsMapping || {})?.map((brandId: string = '') => {
          const { additionalInformation, brandItems = [], brandName, rejected, reason } = brandsMapping[brandId];
          return (
            <Panel
              key={brandName}
              className={rejected ? 'rejected-brand' : styles.panel}
              header={
                <BrandPanel
                  brandId={brandId}
                  isFinalStatus={isFinalStatus}
                  reason={reason}
                  brandName={brandName}
                  rejected={rejected}
                  onBrandClick={handleBrandClick}
                  onReject={setRejectModal}
                  onDelete={() => setDeleteModal({ visible: true, brandId })}
                />
              }
              extra={null}
            >
              <div className={styles.panelContent}>
                {rejected && additionalInformation && (
                  <div className={styles.rejectionInfo}>
                    <Text type="secondary">Additional Information: {additionalInformation}</Text>
                  </div>
                )}
                <BrandItemsTable
                  leadId={id}
                  brandId={brandId}
                  initialItems={brandItems}
                  isFinalStatus={isFinalStatus}
                  rejected={rejected}
                />
              </div>
            </Panel>
          );
        })}
      </Collapse>

      {/* Reject Modal */}
      <RejectBrandModal
        form={brandRejectForm}
        open={!!rejectModal}
        onSubmit={handleRejectBrand}
        onCancel={() => setRejectModal(undefined)}
        rejectionReasons={rejectionReasons}
      />
      {/* Delete Modal */}
      <DeleteBrandModal
        open={deleteModal.visible}
        onOk={handleDeleteBrand}
        onCancel={() => setDeleteModal({ visible: false, brandId: '' })}
        loading={removeLoading}
      />
      <AddBrandToLead
        form={brandAddForm}
        open={openAddBrandToLead}
        onSubmit={handleAddBrandToLead}
        onCancel={() => setOpenAddBrandToLead(false)}
        setOpenBrandModal={() => setAddBrandModalVisible(true)}
      />
      {/* Add Brand Modal */}
      <AddBrandModal
        modalState={addBrandModalVisible}
        onModalStateChange={() => setAddBrandModalVisible((os) => !os)}
      />
    </div>
  );
};
